import { NavLink, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  ClipboardList,
  Server,
  Users,
  UserCog,
  LogOut,
  ShieldCheck,
} from 'lucide-react';
import { useSession } from '@/stores/session';
import { useLogout } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/clientes', label: 'Colegios', icon: Building2 },
  { to: '/auditoria', label: 'Auditoría', icon: ClipboardList, adminOnly: true },
  { to: '/servidores', label: 'Servidores', icon: Server, adminOnly: true },
  { to: '/usuarios', label: 'Usuarios', icon: Users, adminOnly: true },
  { to: '/asignaciones', label: 'Asignaciones', icon: UserCog, adminOnly: true },
];

export function Layout() {
  const user = useSession((s) => s.user)!;
  const logout = useLogout();
  const isAdmin = user.role === 'admin';

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-60 shrink-0 border-r border-ink-700/70 bg-ink-800/60 backdrop-blur md:flex md:flex-col">
        <div className="flex h-16 items-center gap-2 px-5 text-mustard-400">
          <ShieldCheck className="h-6 w-6" />
          <div className="flex flex-col leading-tight">
            <span className="text-xs font-semibold uppercase tracking-[0.18em]">Datacole</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-moss-300/70">Central</span>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 px-2 py-4">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition',
                  isActive
                    ? 'bg-mustard-500/15 text-mustard-300'
                    : 'text-moss-300 hover:bg-ink-700/60 hover:text-moss-200',
                )
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-ink-700/70 p-3">
          <NavLink
            to="/cuenta"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition',
                isActive
                  ? 'bg-ink-700/60 text-moss-200'
                  : 'text-moss-300 hover:bg-ink-700/60 hover:text-moss-200',
              )
            }
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-mustard-500/15 text-xs font-bold text-mustard-300">
              {user.email.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-medium">{user.email}</div>
              <div className="text-[10px] uppercase tracking-wide text-moss-300/60">{user.role}</div>
            </div>
          </NavLink>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between border-b border-ink-700/70 bg-ink-800/70 px-6 backdrop-blur">
          <div className="md:hidden flex items-center gap-2 text-mustard-400">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em]">Datacole Central</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="btn-ghost"
              disabled={logout.isPending}
            >
              <LogOut className="mr-1.5 h-4 w-4" />
              Salir
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
